import CommonTable, { type TableColumn } from "@/components/common/CommonTable";
import MainHeading from "@/components/common/MainHeading";
import Layout from "@/components/layout/Layout";
import {
  Plus,
  Eye,
  MoreHorizontal,
  Trash,
  TriangleAlert,
  Check,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { RoutesLink } from "@/utility/RouteLinks";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useEffect, useState, useCallback } from "react";
import { OdService} from "@/services/Od/OdService";
import { useAppSelector } from "@/store/store";
import TablePagination from "@/components/common/TablePagination";
import type { IPageInfo } from "@/interface/common.interface";
import CommonModal from "@/components/common/CommonModal";
import { showToastNotification } from "@/utility/helper";
import { ThrowError } from "@/api/callApi";
import type { OutdoorDuty, OutdoorDutyParams } from "@/interface/od.interface";


const MyOd: React.FC = () => {
  const navigate = useNavigate();
  const loggedInUser = useAppSelector((state) => state.user.user);

  const [outdoorDuties, setOutdoorDuties] = useState<OutdoorDuty[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  // Delete
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string>("");

  // Status update
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusId, setStatusId] = useState<string>("");
  const [statusAction, setStatusAction] = useState<"APPROVED" | "REJECTED">(
    "APPROVED",
  );
  const [statusRemarks, setStatusRemarks] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleDeleteDialog = useCallback((id: string = "") => {
    setDeleteId(id);
    setDeleteDialogOpen((prev) => !prev);
  }, []);

  const toggleStatusDialog = useCallback(
    (id: string = "", action: "APPROVED" | "REJECTED") => {
      setStatusId(id);
      setStatusAction(action);
      setStatusRemarks("");
      setStatusDialogOpen((prev) => !prev);
    },
    [],
  );

  const handleChangePageSize = (value: number) => {
    setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
  };

  // Fetch data
  const fetchData = async () => {
    if (!loggedInUser?.id) return;

    setLoading(true);
    setError(null);
    try {
      const params: OutdoorDutyParams = {
        userId: loggedInUser.id,
        pageNumber: paginationData.currentPage,
        pageSize: paginationData.pageSize,
      };
      const response = await OdService.getList(params);
      if (response.status && response.data) {
        setOutdoorDuties(response.data.outdoorDuties || []);
        setPaginationData((prev) => ({
          ...prev,
          total: response.data.pageInfo?.total || 0,
          totalPages: response.data.pageInfo?.totalPages || 0,
        }));
      } else {
        setError("Unexpected response format");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load OD records");
    } finally {
      setLoading(false);
    }
  };

  // Delete handler
  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const response = await OdService.delete(deleteId);
      if (response?.status) {
        showToastNotification(
          "success",
          response?.message || "OD deleted successfully",
        );
        toggleDeleteDialog("");
        fetchData();
      } else {
        showToastNotification(
          "danger",
          response?.message || "Failed to delete OD",
        );
      }
    } catch (error) {
      ThrowError(error);
    }
  };

  // Status update handler
  const handleStatusUpdate = async () => {
    if (!statusId) return;
    try {
      setIsSubmitting(true);
      const payload = {
        status: statusAction,
        remarks: statusRemarks,
      };
      const response = await OdService.updateStatus(statusId, payload);
      if (response?.status) {
        showToastNotification(
          "success",
          response?.message || `OD ${statusAction.toLowerCase()} successfully`,
        );
        toggleStatusDialog("", statusAction);
        fetchData();
      } else {
        showToastNotification(
          "danger",
          response?.message || "Failed to update OD status",
        );
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [paginationData.currentPage, paginationData.pageSize, loggedInUser?.id]);

  const columns: TableColumn<OutdoorDuty>[] = [
    {
      key: "date",
      header: "Date",
      render: (record) => record.date || "-",
    },
    {
      key: "location",
      header: "Location",
      render: (record) => record.location || "-",
    },
    {
      key: "purpose",
      header: "Purpose",
      render: (record) => record.reason || "-",
    },
    {
      key: "status",
      header: "Status",
      render: (record) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
          ${
            record.status === "APPROVED"
              ? "bg-green-100 text-green-800"
              : record.status === "PENDING"
                ? "bg-yellow-100 text-yellow-800"
                : record.status === "REJECTED"
                  ? "bg-red-100 text-red-800"
                  : "bg-gray-100 text-gray-800"
          }`}
        >
          {record.status}
        </span>
      ),
    },
    {
      key: "action",
      header: "Action",
      render: (record) => {
        const isPending = record.status === "PENDING";
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-9 w-9 rounded-full hover:bg-slate-100"
              >
                <MoreHorizontal size={18} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => navigate(`${RoutesLink.odDetail}/${record.id}`)}
              >
                <Eye className="mr-2 h-4 w-4" />
                View OD
              </DropdownMenuItem>
              {isPending && (
                <>
                  <DropdownMenuItem
                    onClick={() => toggleStatusDialog(record.id, "APPROVED")}
                    className="text-green-600 focus:text-green-600"
                  >
                    <Check className="mr-2 h-4 w-4" />
                    Approve
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => toggleStatusDialog(record.id, "REJECTED")}
                    className="text-red-600 focus:text-red-600"
                  >
                    <X className="mr-2 h-4 w-4" />
                    Reject
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => toggleDeleteDialog(record.id)}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash className="mr-2 h-4 w-4" />
                    Delete OD
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <MainHeading
          title="My OD Records"
          buttonIcon={<Plus />}
          buttonText="Create OD"
          onClick={() => navigate(RoutesLink.createOd)}
        />
        {loading && <p className="text-sm text-gray-500">Loading...</p>}
        {error && <p className="text-sm text-red-500">{error}</p>}
        {!loading && !error && (
          <>
            <CommonTable
              columns={columns}
              data={outdoorDuties}
              loading={loading}
            />
            <TablePagination
              paginationData={paginationData}
              setPaginationData={setPaginationData}
              handleChangePageSize={handleChangePageSize}
              isLoading={loading}
            />
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <CommonModal
        show={deleteDialogOpen}
        onClose={() => toggleDeleteDialog("")}
        title=""
        className="max-w-md rounded-2xl p-0 overflow-hidden"
      >
        <div className="p-6">
          <div className="flex flex-col items-center text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <TriangleAlert className="text-red-600 w-8 h-8" />
            </div>
            <h2 className="mt-5 text-2xl font-bold text-gray-900">
              Delete OD Request?
            </h2>
            <p className="mt-3 text-sm leading-6 text-gray-500">
              This action cannot be undone. Once deleted, the OD application
              will be permanently removed.
            </p>
          </div>
          <div className="mt-8 flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => toggleDeleteDialog("")}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={handleDelete}
            >
              Delete
            </Button>
          </div>
        </div>
      </CommonModal>

      {/* Status Update Modal */}
      <CommonModal
        show={statusDialogOpen}
        onClose={() => toggleStatusDialog("", "APPROVED")}
        title=""
        className="max-w-md rounded-2xl p-0 overflow-hidden"
      >
        <div className="p-6">
          <div className="flex flex-col items-center text-center">
            <div
              className={`flex h-16 w-16 items-center justify-center rounded-full ${statusAction === "APPROVED" ? "bg-green-100" : "bg-red-100"}`}
            >
              {statusAction === "APPROVED" ? (
                <Check className="text-green-600 w-8 h-8" />
              ) : (
                <X className="text-red-600 w-8 h-8" />
              )}
            </div>
            <h2 className="mt-5 text-2xl font-bold text-gray-900">
              {statusAction === "APPROVED"
                ? "Approve OD Request"
                : "Reject OD Request"}
            </h2>
            <p className="mt-3 text-sm leading-6 text-gray-500">
              {statusAction === "APPROVED"
                ? "You are about to approve this OD request. Please add any remarks if needed."
                : "You are about to reject this OD request. Please provide a reason for rejection."}
            </p>
          </div>
          <div className="mt-6">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">
              Remarks (optional)
            </label>
            <textarea
              value={statusRemarks}
              onChange={(e) => setStatusRemarks(e.target.value)}
              placeholder="Enter remarks..."
              className="w-full min-h-[80px] text-sm p-3 bg-white border border-slate-200 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
            />
          </div>
          <div className="mt-6 flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => toggleStatusDialog("", statusAction)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant={statusAction === "APPROVED" ? "default" : "destructive"}
              className="flex-1"
              onClick={handleStatusUpdate}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Updating..."
                : statusAction === "APPROVED"
                  ? "Approve"
                  : "Reject"}
            </Button>
          </div>
        </div>
      </CommonModal>
    </Layout>
  );
};

export default MyOd;
